/* eslint-disable
  no-cond-assign,
  no-param-reassign,
  no-underscore-dangle,
  no-restricted-syntax,
  ssr-friendly/no-dom-globals-in-constructor
*/

import { debounce } from 'lodash';

/**
 * This class is used for both table sticky header and horizontal scroll support on tables.
 *
 * Copied from https://github.com/archfz/vh-sticky-table-header
  At version 1.8.2 SHA 1eaa720bf3f763f2cc74b677d808dadd1de618a9
 */
export default class StickyTableHeader {
  /** the original `<newtable>` node */
  tableContainer: HTMLTableElement;

  /** the original scrollable wrapper */
  tableContainerParent: HTMLDivElement;

  /** the floating clone `<thead>`  */
  cloneHeader: any = null;

  /** head clone `<table>` container */
  headCloneContainer: HTMLTableElement;

  /** head clone `<scrollablewrapper class="head">` */
  headCloneContainerParent: HTMLDivElement;

  /** the floating clone `<tfoot>` */
  cloneFooter: any = null;

  /** foot clone `<table>` container */
  footCloneContainerParent: HTMLDivElement;

  /** foot clone `<scrollablewrapper class="foot">` */
  footCloneContainer: HTMLTableElement;

  stickyScrollEventHandler?: EventListener;

  containerScrollEventHandler?: EventListener;

  resizeObserver?: ResizeObserver;

  mutationObserver?: MutationObserver;

  tableContainerWidth = 0;

  currentFrameRequest?: number;

  clickListener?: (event: MouseEvent) => void;

  /** document.scrollingElement, which is the `<html>` tag */
  scrollParent: HTMLElement;

  /** the original `<thead>` node */
  header: HTMLTableRowElement;

  /** the original `<tfoot>` node */
  footer?: HTMLTableRowElement | null;

  /** the position of the floating `<thead>` element in relation to the table */
  headerPosition?: 'above' | 'below' | 'inside';

  /** the position of the floating `<tfoot>` element in relation to the table */
  footerPosition?: 'above' | 'below' | 'inside';

  /** the position where the header should stick, in pixels starting from the top of the screen */
  top: { max: number | string; [key: number]: number | string };

  constructor({
    tableContainer,
    headCloneContainer,
    footCloneContainer,
    offsetTop,
  }: {
    tableContainer: HTMLTableElement;
    headCloneContainer: HTMLTableElement;
    footCloneContainer: HTMLTableElement;
    offsetTop: number;
  }) {
    this.tableContainer = tableContainer;
    this.headCloneContainer = headCloneContainer;
    this.footCloneContainer = footCloneContainer;
    this.top = { max: offsetTop };

    const header = tableContainer.querySelector<HTMLTableRowElement>('thead');
    const footer = tableContainer.querySelector<HTMLTableRowElement>('tfoot');

    if (!header || !this.tableContainer.parentNode) {
      throw new Error('Header or parent node of sticky header table container not found!');
    }

    this.tableContainerParent = this.tableContainer.parentNode as HTMLDivElement;
    this.headCloneContainerParent = this.headCloneContainer.parentNode as HTMLDivElement;
    this.footCloneContainerParent = this.footCloneContainer.parentNode as HTMLDivElement;

    this.header = header;
    this.footer = footer;
    this.scrollParent = document.scrollingElement as HTMLElement;

    this.setup();
  }

  setup(): void {
    this.setupStickyScrollHandler();
    this.setupResizeObserverErrorHandler();
    this.setupResizeObserver();
    this.setupMutationObserver();
    this.setupHeaderClickEventMirroring();
    this.setupCloneHorizontalScrollMirroring();
  }

  resizeObserverErrorHandler(event: ErrorEvent): void {
    if (event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }

  setupResizeObserverErrorHandler(): void {
    window.addEventListener('error', this.resizeObserverErrorHandler);
  }

  setupHeaderClickEventMirroring(): void {
    this.clickListener = (event: MouseEvent) => {
      const cloneRect = this.cloneHeader.getBoundingClientRect();
      const distX = event.clientX - cloneRect.x;
      const distY = event.clientY - cloneRect.y;

      const scrollParent = this.scrollParent;

      (scrollParent as any)._save_scroll = scrollParent.scrollTop;

      this.header.style.setProperty('scroll-margin-top', `${this.getTableTop() + 3}px`);
      this.header.scrollIntoView({ behavior: 'instant' as any, block: 'start' });

      const headerRect = this.header.getBoundingClientRect();

      const hiddenTargets: HTMLElement[] = [];
      let target: Element | null;

      do {
        target = document.elementFromPoint(headerRect.x + distX, headerRect.y + distY);

        if (target && !this.header.contains(target)) {
          // @TODO: Switch this solution to scroll-margin-top one as that is less intrusive.
          // Possible to switch once chrome issue is fixed: https://issues.chromium.org/issues/40074749
          (target as HTMLElement).style.visibility = 'collapse';
          hiddenTargets.push(target as HTMLElement);
        }
      } while (target && !this.header.contains(target) && hiddenTargets.length < 10);

      if (target) {
        target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      }

      hiddenTargets.forEach(t => t.style.removeProperty('visibility'));
      scrollParent.scrollTo({ behavior: 'instant' as any, top: (scrollParent as any)._save_scroll });
    };
    this.headCloneContainer.addEventListener('click', this.clickListener);
  }

  setupStickyScrollHandler(): void {
    if (this.headCloneContainerParent.parentNode) {
      (this.headCloneContainerParent.parentNode as HTMLElement).style.position = 'relative';
    }

    const updateSticky = () => {
      this.currentFrameRequest = window.requestAnimationFrame(() => {
        this.updateHeaderPosition();
        this.updateFooterPosition();
      });
    };

    updateSticky();

    this.stickyScrollEventHandler = () => updateSticky();

    window.addEventListener('scroll', this.stickyScrollEventHandler);
  }

  resizeEventHandler(): void {
    this.currentFrameRequest = window.requestAnimationFrame(() => {
      const headerSize = this.header.getBoundingClientRect().width;
      this.headCloneContainer.style.width = `${headerSize}px`;
      this.footCloneContainer.style.width = `${headerSize}px`;

      this.headCloneContainerParent.style.top = `${this.getTableTop()}px`;
      this.footCloneContainerParent.style.bottom = `${0}px`;

      this.setHorizontalScrollOnCloneContainer(this.headCloneContainerParent);
      this.setHorizontalScrollOnCloneContainer(this.footCloneContainerParent);
    });
  }

  resizeObserverCallback: ResizeObserverCallback = entry => {
    const width = Math.max(...entry.map(e => e.contentRect.width));
    if (this.tableContainerWidth === width) {
      // No change in width, no need to update
      return;
    }
    this.resizeEventHandler();
    this.tableContainerWidth = width;
  };

  setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(debounce(this.resizeObserverCallback));
    this.resizeObserver.observe(this.tableContainerParent);
  }

  setupCloneHorizontalScrollMirroring(): void {
    this.containerScrollEventHandler = e => {
      window.requestAnimationFrame(() => {
        this.setHorizontalScrollOnCloneContainer(this.headCloneContainerParent);
        this.setHorizontalScrollOnCloneContainer(this.footCloneContainerParent);
      });
    };

    this.tableContainerParent.addEventListener('scroll', this.containerScrollEventHandler);
  }

  createHeaderClone(element: HTMLElement, extraStyles: Record<string, string>): HTMLTableRowElement {
    const clone = element.cloneNode(true) as HTMLTableRowElement;

    this.headCloneContainer.append(clone);

    const headerSize = element.getBoundingClientRect().width;

    this.headCloneContainer.style.display = 'table';
    this.headCloneContainer.style.width = `${headerSize}px`;

    this.headCloneContainerParent.style.position = 'fixed';
    this.headCloneContainerParent.style.overflow = 'hidden';

    Object.entries(extraStyles).forEach(([key, value]) => {
      this.headCloneContainerParent.style[key] = value;
    });

    this.setHorizontalScrollOnCloneContainer(this.headCloneContainer);

    return clone;
  }

  createFooterClone(element: HTMLElement | null, extraStyles: Record<string, string>): HTMLTableRowElement | undefined {
    if (!element) {
      return;
    }
    const clone = element.cloneNode(true) as HTMLTableRowElement;
    const footerSize = element.getBoundingClientRect().width;

    this.footCloneContainer.append(clone);

    this.footCloneContainer.style.display = 'table';
    this.footCloneContainer.style.width = `${footerSize}px`;

    Object.entries(extraStyles).forEach(([key, value]) => {
      this.footCloneContainerParent.style[key] = value;
    });

    this.footCloneContainerParent.style.position = 'fixed';
    this.footCloneContainerParent.style.overflow = 'hidden';
    this.footCloneContainerParent.style.bottom = `${0}px`;

    Object.entries(extraStyles).forEach(([key, value]) => {
      this.footCloneContainerParent.style[key] = value;
    });

    this.setHorizontalScrollOnCloneContainer(this.footCloneContainerParent);

    return clone;
  }

  setupMutationObserver(): void {
    this.mutationObserver = new MutationObserver(() => {
      this.resetClones();
    });

    const config = {
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'data-freeze'],
      subtree: true,
    };

    this.mutationObserver.observe(this.header, config);
    if (this.footer) {
      this.mutationObserver.observe(this.footer, config);
    }
  }

  resetClones = debounce(() => {
    this.destroy();
    this.setup();
  }, 100);

  setHorizontalScrollOnCloneContainer(container: HTMLElement): void {
    container.style.width = `${this.tableContainerParent.getBoundingClientRect().width}px`;
    container.scrollLeft = this.tableContainerParent.scrollLeft;
  }

  updateHeaderPosition(): void {
    const tableRect = this.tableContainer.getBoundingClientRect();
    const tableOffsetTop = this.tableContainer.offsetTop;
    const tableTop = tableRect.y;
    const tableBottom = this.getTableBottom(tableRect);

    const diffTop = -tableTop;
    const diffBottom = -tableBottom;
    const topPx = this.getTableTop();

    const headerIsAboveTable = diffTop <= -topPx;
    const headerIsBelowTable = diffBottom > -topPx;
    const headerIsInsideTable = !(headerIsAboveTable || headerIsBelowTable);

    if (headerIsInsideTable && !this.cloneHeader) {
      this.cloneHeader = this.createHeaderClone(this.header, { top: `${topPx}px` });
      this.headCloneContainerParent.style.display = 'none';
    }

    if (this.cloneHeader) {
      if (headerIsInsideTable) {
        this.headerPosition = 'inside';
        this.headCloneContainerParent.style.display = 'block';
        this.headCloneContainerParent.style.position = 'fixed';
        this.headCloneContainerParent.style.top = `${topPx}px`;
        this.setHorizontalScrollOnCloneContainer(this.headCloneContainerParent);
      } else if (headerIsBelowTable) {
        this.headerPosition = 'below';
        this.headCloneContainerParent.style.display = 'block';
        this.headCloneContainerParent.style.position = 'absolute';
        this.headCloneContainerParent.style.top = `${tableBottom - tableTop + tableOffsetTop}px`;
      } else {
        this.headerPosition = 'above';
        this.headCloneContainerParent.style.display = 'none';
        this.removeHeaderClone();
      }
    }
  }

  updateFooterPosition(): void {
    if (!this.footer) {
      return;
    }
    const originalFootRect = this.footer.getBoundingClientRect();

    const originalFootBottom = originalFootRect.bottom;

    const tableRect = this.tableContainer.getBoundingClientRect();
    const tableTop = tableRect.y;
    const footHeight = originalFootRect.height;

    const footerIsAboveTable = window.innerHeight < tableTop + footHeight * 2;
    const footerIsBelowTable = originalFootBottom < window.innerHeight;
    const footerIsInsideTable = !(footerIsAboveTable || footerIsBelowTable);

    if (footerIsInsideTable && !this.cloneFooter) {
      this.footCloneContainerParent.style.display = 'none';
      this.cloneFooter = this.createFooterClone(this.footer, { bottom: `0px` });
    }

    if (this.cloneFooter) {
      if (footerIsInsideTable) {
        this.footerPosition = 'inside';
        this.footCloneContainerParent.style.display = 'block';
        this.footCloneContainerParent.style.position = 'fixed';
        this.footCloneContainerParent.style.top = '';
        this.footCloneContainerParent.style.bottom = `0px`;
        this.setHorizontalScrollOnCloneContainer(this.footCloneContainerParent);
      } else if (footerIsAboveTable) {
        this.footerPosition = 'above';
        this.footCloneContainerParent.style.display = 'block';
        this.footCloneContainerParent.style.position = 'absolute';
        this.footCloneContainerParent.style.top = `${footHeight}px`;
        this.footCloneContainerParent.style.bottom = '';
      } else {
        this.footerPosition = 'below';
        this.footCloneContainerParent.style.display = 'none';
        this.removeFooterClone();
      }
    }
  }

  sizeToPx(size: number | string): number {
    if (typeof size === 'number') {
      return size;
    }
    if (size.match(/rem$/)) {
      const rem = +size.replace(/rem$/, '');
      return Number.parseFloat(window.getComputedStyle(document.getElementsByTagName('html')[0]).fontSize) * rem;
    }
    console.error('Unsupported size format for sticky table header displacement.');
    return 0;
  }

  getTableTop(): number {
    const windowWidth = document.body.getBoundingClientRect().width;

    const sizes = Object.entries(this.top)
      .filter(([key]) => key !== 'max')
      .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10));

    for (const [breakpoint, value] of sizes) {
      if (windowWidth < parseInt(breakpoint, 10)) {
        return this.sizeToPx(value);
      }
    }

    const top = this.sizeToPx(this.top.max);
    const { top: parentTop } = this.scrollParent.getBoundingClientRect();

    return Math.max(top, parentTop);
  }

  getTableBottom(tableRect: DOMRect): number {
    const headerHeight = this.header.getBoundingClientRect().height;
    return tableRect.bottom - 2 * headerHeight;
  }

  removeHeaderClone(): void {
    if (this.cloneHeader?.isConnected) {
      this.headCloneContainer.removeChild(this.cloneHeader);
      this.cloneHeader = null;
    }
  }

  removeFooterClone(): void {
    if (this.cloneFooter?.isConnected) {
      this.footCloneContainer.removeChild(this.cloneFooter);
      this.cloneFooter = null;
    }
  }

  destroy(): void {
    if (this.stickyScrollEventHandler) {
      window.removeEventListener('scroll', this.stickyScrollEventHandler);
    }
    if (this.currentFrameRequest) {
      window.cancelAnimationFrame(this.currentFrameRequest);
    }
    if (this.containerScrollEventHandler) {
      this.tableContainerParent.removeEventListener('click', this.containerScrollEventHandler);
    }
    if (this.clickListener) {
      this.headCloneContainer.removeEventListener('click', this.clickListener);
    }

    this.removeHeaderClone();
    this.removeFooterClone();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    if (this.resizeObserverErrorHandler) {
      window.removeEventListener('error', this.resizeObserverErrorHandler);
    }
  }
}
